import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { QuotaPeriod } from "../../api/settingsApi";

interface Quota {
  platform: string;
  used: number;
  limit?: number | null;
  available?: number | null;
  isUnlimited?: boolean;
}

interface QueueStatusCardProps {
  pendingJobs: number;
  processedToday: number;
}

export const QueueStatusCard = ({ pendingJobs, processedToday }: QueueStatusCardProps) => (
  <Card className="border-neutral-800 bg-neutral-900/50">
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold text-neutral-400">Estado de la cola</CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="rounded-md border border-neutral-700 bg-neutral-800/50 p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-neutral-300">Trabajos pendientes</span>
          <span className="text-2xl font-bold text-purple-400">{pendingJobs}</span>
        </div>
        <p className="mt-2 text-xs text-neutral-500">
          {pendingJobs === 0
            ? "No hay trabajos en cola para procesar"
            : `${pendingJobs} ${pendingJobs === 1 ? "trabajo" : "trabajos"} esperando sincronización`}
        </p>
      </div>
      <div className="rounded-md border border-neutral-700 bg-neutral-800/50 p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-neutral-300">Trabajos completados hoy</span>
          <span className="text-2xl font-bold text-green-400">{processedToday}</span>
        </div>
        <p className="mt-2 text-xs text-neutral-500">
          {processedToday
            ? `${processedToday} ${processedToday === 1 ? "trabajo" : "trabajos"} completados en las últimas 24 horas`
            : "Aún no se completaron trabajos hoy"}
        </p>
      </div>
    </CardContent>
  </Card>
);

interface QuotasCardProps {
  quotas: Quota[];
  quotaPeriod: QuotaPeriod;
  onPeriodChange: (period: QuotaPeriod) => void;
}

const PERIOD_LABELS: Record<QuotaPeriod, string> = {
  daily: "Hoy",
  weekly: "Últimos 7 días",
  monthly: "Último mes",
  yearly: "Último año",
};

export const QuotasCard = ({ quotas, quotaPeriod, onPeriodChange }: QuotasCardProps) => (
  <Card className="border-neutral-800 bg-neutral-900/50">
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between">
        <CardTitle className="text-sm font-semibold text-neutral-400">Cuotas de API</CardTitle>
        <Select value={quotaPeriod} onValueChange={(v) => onPeriodChange(v as QuotaPeriod)}>
          <SelectTrigger className="w-40 border-neutral-700 bg-neutral-800/50 text-neutral-200 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-neutral-700 bg-neutral-900">
            <SelectItem value="daily" className="text-neutral-200 focus:bg-neutral-800">
              Hoy
            </SelectItem>
            <SelectItem value="weekly" className="text-neutral-200 focus:bg-neutral-800">
              Últimos 7 días
            </SelectItem>
            <SelectItem value="monthly" className="text-neutral-200 focus:bg-neutral-800">
              Último mes
            </SelectItem>
            <SelectItem value="yearly" className="text-neutral-200 focus:bg-neutral-800">
              Último año
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </CardHeader>
    <CardContent className="space-y-3">
      {quotas.length === 0 ? (
        <div className="rounded-md border border-neutral-700 bg-neutral-800/50 p-4 text-center text-xs text-neutral-500">
          No hay cuotas registradas para {PERIOD_LABELS[quotaPeriod].toLowerCase()}
        </div>
      ) : (
        quotas.map((quota) => {
          if (quota.isUnlimited) {
            return (
              <div
                key={quota.platform}
                className="rounded-md border border-neutral-700 bg-neutral-800/50 p-3"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase text-neutral-300">
                    {quota.platform}
                  </span>
                  <span className="text-sm font-bold text-blue-400">∞ Ilimitado</span>
                </div>
                <p className="text-xs text-neutral-500">
                  {quota.used.toLocaleString("es-AR")} requests en{" "}
                  {PERIOD_LABELS[quotaPeriod].toLowerCase()}
                </p>
              </div>
            );
          }

          const percentage = (quota.used / (quota.limit ?? 1)) * 100;
          const isLow = percentage > 80;

          return (
            <div
              key={quota.platform}
              className="rounded-md border border-neutral-700 bg-neutral-800/50 p-3"
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase text-neutral-300">
                  {quota.platform}
                </span>
                <span className={`text-sm font-bold ${isLow ? "text-red-400" : "text-green-400"}`}>
                  {(quota.available ?? 0).toLocaleString("es-AR")} disponibles
                </span>
              </div>
              <div className="mb-1 h-2 overflow-hidden rounded-full bg-neutral-800">
                <div
                  className={`h-full transition-all ${isLow ? "bg-red-500" : "bg-green-500"}`}
                  style={{ width: `${Math.min(100, percentage)}%` }}
                />
              </div>
              <p className="text-xs text-neutral-500">
                {quota.used.toLocaleString("es-AR")} / {(quota.limit ?? 0).toLocaleString("es-AR")}{" "}
                usados ({percentage.toFixed(1)}%)
              </p>
            </div>
          );
        })
      )}
    </CardContent>
  </Card>
);
